/**
 * Notification Context
 * 
 * Provides toast notifications for user feedback.
 * Uses notistack for notification management.
 */

import React, { createContext, useContext } from 'react';
import { SnackbarProvider, useSnackbar, VariantType } from 'notistack';

/**
 * Notification context value
 */
interface NotificationContextValue {
  showSuccess: (message: string) => void;
  showError: (message: string) => void;
  showWarning: (message: string) => void;
  showInfo: (message: string) => void;
}

const NotificationContext = createContext<NotificationContextValue | undefined>(undefined);

/**
 * Inner component that uses notistack hook
 */
const NotificationProviderInner: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { enqueueSnackbar } = useSnackbar();

  const showNotification = (message: string, variant: VariantType) => {
    enqueueSnackbar(message, {
      variant,
      autoHideDuration: variant === 'error' ? 6000 : 4000,
      anchorOrigin: {
        vertical: 'bottom',
        horizontal: 'right',
      },
    });
  };

  const value: NotificationContextValue = {
    showSuccess: (message: string) => showNotification(message, 'success'),
    showError: (message: string) => showNotification(message, 'error'),
    showWarning: (message: string) => showNotification(message, 'warning'),
    showInfo: (message: string) => showNotification(message, 'info'),
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

/**
 * Notification Provider
 * Wraps the app with notification functionality
 */
export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <SnackbarProvider
      maxSnack={3}
      preventDuplicate
      dense
    >
      <NotificationProviderInner>{children}</NotificationProviderInner>
    </SnackbarProvider>
  );
};

/**
 * Hook to use notifications
 */
export const useNotifications = (): NotificationContextValue => {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

